import moment from "moment";
import { AuthBase } from "../../../plunk/service/auth-base";
import { parseClaims } from "../../auth/cognito";
import endpoints from "../../endpoints";
import account from "../../data/account";
import { parseJson, idStr, normalizeLists, audienceApi, toast, num, asDate, dateTime, dateOnly, ago, isAdmin } from "../email-campaigns/api";
export { idStr, normalizeLists, audienceApi, toast, num, asDate, dateTime, dateOnly, ago, isAdmin };

/*
 * Outreach (cold B2B sequencer, OUTREACH_2026-10-02.md §11): the raw authenticated client for /v1/outreach/*.
 *
 * Same pattern as the Email campaigns client: bearer fetch with the signed-in user's token + the x-app-id
 * workspace header (every route requires a valid token since 2026-10-07; the workspace comes from the token's
 * tenant claim). Responses parse with json-bigint (storeAsString) so audience / person ids above 2^53 stay exact.
 *
 * GroupAuthorize("Administrator") routes (program approve/reject, domain create/retire, mailbox create/disable)
 * authorize on the ACCESS token's cognito:groups, so those calls pass {admin: true}.
 *
 * Errors: the API answers {error, message} for gates and transitions, {error, funnel} for a refused audience
 * (422 consumerAudience) and {error: "<sentence>", code: "outreach_not_enabled"} (403) for a workspace without
 * Outreach. The thrown Error carries status, code and the parsed body; errText() renders it verbatim.
 */

function tenantId() {
  return (account.get() || {}).tenantId || "";
}

// Ids above 2^53 travel as unquoted number literals (the API binds long; a JS number would lose digits).
const BIG_KEYS = ["audienceId", "personIds", "repPersonId", "personaPersonId", "mailboxIds", "mailboxId", "domainId"];
function bodyText(body) {
  if (body === undefined) return undefined;
  if (typeof body === "string") return body;
  let s = JSON.stringify(body);
  BIG_KEYS.forEach(k => {
    s = s.replace(new RegExp(`"${k}":"(\\d+)"`, "g"), `"${k}":$1`);
    s = s.replace(new RegExp(`"${k}":\\[([^\\]]*)\\]`, "g"), (m, inner) => `"${k}":[${inner.replace(/"(\d+)"/g, "$1")}]`);
  });
  return s;
}
async function rawFetch(url, method, body, opts = {}) {
  const bearer = opts.admin ? AuthBase.accessToken || AuthBase.idToken : AuthBase.idToken || AuthBase.accessToken;
  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + (bearer || ""),
    "x-app-id": tenantId()
  };
  const res = await fetch(url, {
    method,
    headers,
    mode: "cors",
    body: bodyText(body)
  });
  if (res.status === 401 && !opts._retried) {
    try {
      await AuthBase.refreshTokensAsync();
    } catch (e) {/* fall through to the error below */}
    return rawFetch(url, method, body, {
      ...opts,
      _retried: true
    });
  }
  const text = await res.text();
  let data = null;
  try {
    data = text ? parseJson(text) : null;
  } catch (e) {
    data = text;
  }
  if (!res.ok) {
    const obj = data && typeof data === "object" ? data : null;
    const code = obj ? obj.code || (typeof obj.error === "string" && obj.error.length <= 40 && !/\s/.test(obj.error) ? obj.error : null) : null;
    const msg = obj ? obj.message || (typeof obj.error === "string" && /\s/.test(obj.error) ? obj.error : null) : typeof data === "string" && data.length < 300 ? data : null;
    const err = new Error(msg || code || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = code;
    err.data = obj;
    throw err;
  }
  return data;
}

/** /v1/outreach{path} */
export function api(method, path, body, opts) {
  return rawFetch(`${endpoints.product.root}/outreach${path}`, method, body, opts);
}
export function qs(obj) {
  const parts = [];
  Object.keys(obj || {}).forEach(k => {
    const v = obj[k];
    if (v === undefined || v === null || v === "" || v === "all") return;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  });
  return parts.length ? "?" + parts.join("&") : "";
}
export const isNotEnabled = e => !!e && e.status === 403 && e.code === "outreach_not_enabled";
const CODE_TEXT = {
  invalidTransition: "That action is not available from the program's current status.",
  needsReview: null,
  needsSteps: null,
  needsMailboxes: null,
  needsLeads: null,
  mailboxesNotActive: null,
  lintFailed: null,
  needsAddress: null,
  consumerAudience: "This audience is refused: it is mostly consumers. Outreach only sends to business contacts.",
  locked: null,
  running: null,
  exists: "That already exists.",
  notImplemented: null,
  notConfigured: null
};

/** The inline message for an API error: the server's own message verbatim, with the code shown beside it. */
export function errText(e, fallback) {
  if (!e) return fallback || "Something went wrong.";
  if (isNotEnabled(e)) return "Outreach is not enabled for this workspace.";
  const code = e.code;
  const known = code && Object.prototype.hasOwnProperty.call(CODE_TEXT, code) ? CODE_TEXT[code] : null;
  const msg = typeof e.message === "string" && e.message && !/^HTTP \d+$/.test(e.message) && e.message !== code ? e.message : null;
  if (msg) return code ? `${msg} (${code})` : msg;
  if (known) return known;
  if (code) return `${code}${e.status ? ` (HTTP ${e.status})` : ""}`;
  if (e.status === 403) return "You don't have permission for that (Administrator only).";
  if (e.status === 404) return "Not found.";
  if (e.status === 401) return "Your session expired. Sign in again.";
  return fallback || (e.status ? `Request failed (HTTP ${e.status}).` : "Something went wrong.");
}

// ---------------------------------------------------------------- vocab

export const PROGRAM_STATUSES = ["Draft", "InReview", "Approved", "Running", "Paused", "Completed", "Canceled"];
export function statusLabel(s) {
  return {
    InReview: "In review"
  }[s] || s || "-";
}

/** A stored actor (submittedBy / reviewedBy) for display: emails as they are; the signed-in user's own id as "you";
 * any other bare user id as the fallback (programs reviewed before 2026-10-08 stored the token's user id, not an email). */
export function who(v, fallback) {
  if (!v) return fallback;
  if (String(v).indexOf("@") > -1) return String(v);
  let sub = null;
  try {
    sub = (parseClaims(AuthBase.idToken) || {}).sub || null;
  } catch (e) {
    sub = null;
  }
  return sub && String(v) === sub ? "you" : fallback;
}
export function statusTone(s) {
  switch (s) {
    case "Running":
      return "success";
    case "Approved":
      return "info";
    case "InReview":
    case "Paused":
      return "warning";
    case "Canceled":
    case "Rejected":
      return "danger";
    default:
      return "neutral";
  }
}
export function mailboxTone(s) {
  switch (s) {
    case "active":
      return "success";
    case "warming":
      return "info";
    case "paused":
    case "provisioning":
      return "warning";
    case "disabled":
      return "danger";
    default:
      return "neutral";
  }
}
export function domainTone(s) {
  switch (s) {
    case "active":
    case "verified":
      return "success";
    case "warming":
      return "info";
    case "dns_pending":
    case "ordered":
    case "paused":
      return "warning";
    case "retired":
      return "danger";
    default:
      return "neutral";
  }
}
export const DOMAIN_STATUS_LABEL = {
  dns_pending: "DNS pending",
  ordered: "ordered",
  verified: "verified",
  warming: "warming",
  active: "active",
  paused: "paused",
  retired: "retired"
};
export const LEAD_STATUSES = ["queued", "in_sequence", "replied", "interested", "not_interested", "ooo", "bounced", "unsubscribed", "completed", "stopped", "refused"];
export function leadLabel(s) {
  return String(s || "-").replace(/_/g, " ").replace(/^ooo$/, "out of office");
}
export function leadTone(s) {
  switch (s) {
    case "interested":
      return "success";
    case "in_sequence":
    case "queued":
    case "replied":
      return "info";
    case "ooo":
    case "stopped":
      return "warning";
    case "bounced":
    case "unsubscribed":
    case "refused":
    case "not_interested":
      return "danger";
    default:
      return "neutral";
  }
}
export const CLASSES = ["interested", "question", "other", "ooo", "not_interested", "wrong_person", "unsubscribe", "bounce"];
export function classLabel(c) {
  return {
    ooo: "Out of office",
    not_interested: "Not interested",
    wrong_person: "Wrong person",
    interested: "Interested",
    question: "Question",
    other: "Other",
    unsubscribe: "Unsubscribe",
    bounce: "Bounce"
  }[c] || c || "Unclassified";
}
export function classTone(c) {
  switch (c) {
    case "interested":
      return "success";
    case "question":
      return "info";
    case "ooo":
    case "other":
    case "wrong_person":
      return "warning";
    case "not_interested":
    case "unsubscribe":
    case "bounce":
      return "danger";
    default:
      return "neutral";
  }
}
export const PROVIDER_LABEL = {
  m365: "Microsoft 365",
  google: "Google Workspace",
  smtp: "SMTP"
};

// ---------------------------------------------------------------- formatting

/** Rate from the API (a fraction, 0.0123) → "1.2 %". */
export function rate(v, d = 1) {
  const n = Number(v);
  return isFinite(n) ? `${(n * 100).toFixed(d)} %` : "-";
}
export function ratio(a, b, d = 1) {
  const x = Number(a) || 0,
    y = Number(b) || 0;
  return y > 0 ? `${(x / y * 100).toFixed(d)} %` : "-";
}

/**
 * Global range (plunk-global-range vocabulary) → the outreach stats grammar: today | 7d | 14d | 30d | 90d | all |
 * "YYYY-MM-DD,YYYY-MM-DD".
 */
export function rangeToApi(range) {
  const m = moment();
  const span = (a, b) => `${a.format("YYYY-MM-DD")},${b.format("YYYY-MM-DD")}`;
  switch (range) {
    case "today":
      return "today";
    case "yesterday":
      return span(m.clone().subtract(1, "day"), m.clone().subtract(1, "day"));
    case "last_7_days":
      return "7d";
    case "this_week":
      return span(m.clone().startOf("week"), m);
    case "last_week":
      return span(m.clone().subtract(1, "week").startOf("week"), m.clone().subtract(1, "week").endOf("week"));
    case "this_month":
      return span(m.clone().startOf("month"), m);
    case "last_month":
      return span(m.clone().subtract(1, "month").startOf("month"), m.clone().subtract(1, "month").endOf("month"));
    case "this_quarter":
      return span(m.clone().startOf("quarter"), m);
    case "last_quarter":
      return span(m.clone().subtract(1, "quarter").startOf("quarter"), m.clone().subtract(1, "quarter").endOf("quarter"));
    case "this_year":
      return span(m.clone().startOf("year"), m);
    case "last_year":
      return span(m.clone().subtract(1, "year").startOf("year"), m.clone().subtract(1, "year").endOf("year"));
    case "all_time":
      return "all";
    default:
      {
        const c = /^custom_(\d{8})_(\d{8})$/.exec(String(range || ""));
        if (c) return span(moment(c[1], "YYYYMMDD"), moment(c[2], "YYYYMMDD"));
        return "30d";
      }
  }
}

/** Parse a JSON column that may already be an object. */
export function parseMaybe(v) {
  if (!v) return null;
  if (typeof v === "object") return v;
  try {
    return parseJson(v);
  } catch (e) {
    return null;
  }
}

/** "Mon to Fri 08:00 to 17:00 recipient time" from a window spec. */
const DAY = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export function windowLabel(w) {
  if (!w) return "";
  const days = Array.from(new Set((w.days || []).map(Number))).sort();
  let d = days.map(x => DAY[x]).join(", ");
  if (days.length > 2 && days.every((x, i) => i === 0 || x === days[i - 1] + 1)) d = `${DAY[days[0]]} to ${DAY[days[days.length - 1]]}`;
  return `${d} ${w.start || "08:00"} to ${w.end || "17:00"} ${w.tz === "recipient" || !w.tz ? "recipient time" : w.tz}`;
}

/** Steps → a human sequence summary: "3 emails over 9 business days". */
export function sequenceLabel(steps) {
  const s = steps || [];
  const emails = s.filter(x => x.kind === "email").length;
  const days = s.filter(x => x.kind === "wait").reduce((a, x) => a + (Number(x.waitDays) || 0), 0);
  if (!emails) return "no email steps yet";
  return `${emails} email${emails === 1 ? "" : "s"}${days ? ` over ${days} day${days === 1 ? "" : "s"}` : ""}`;
}
export const OUTREACH_VIEWS_BASE = "/Email/outreach";